import React, { useEffect, useState, useMemo } from 'react';
import ProductCard from '../components/shop/organisms/ProductCard';
import { useNavigate } from "react-router-dom";
import styled from 'styled-components';
import { Heading1 } from '../components/typography';
import Navigation from '../components/commons/organisms/Navigation';
import APIService from '../utils/api';
import CategorySlider from '../components/shop/organisms/CategorySlider';
import SearchBar from '../components/shop/organisms/SearchBar';
import StorageService from '../utils/storage';
import { useCart } from '../contexts/CartContext';
import Skeleton from 'react-loading-skeleton';
import { LIGHT_PINK_ORANGE, PALE, SOFT_TOMATO } from '../components/colors';
import usePageView from '../hooks/use-page-view';
import { MIXPANEL_ACTION, MIXPANEL_PAGE } from '../utils/constants';
import MixpanelService from '../utils/mixpanel';
import HeroBanner from '../components/shop/organisms/HeroBanner';
import { useMediaQuery } from 'react-responsive';

const desktopSlides = [
  { id: 1, image: require('../assets/banners/1.png'), url: '/shop' },
  { id: 2, image: require('../assets/banners/2.png'), url: '/shop/glutashin-1200-' },
  { id: 3, image: require('../assets/banners/3.png'), url: '/shop/lemon-bottle' },
  { id: 4, image: require('../assets/banners/4.png'), url: '/shop/rentox-100-units' },
  { id: 5, image: require('../assets/banners/5.png'), url: '/shop/botulax-100-units' },
  { id: 6, image: require('../assets/banners/6.png'), url: '/shop/glutanex-tab' },
  { id: 7, image: require('../assets/banners/7.png'), url: '/shop/cartinex' },
  
  // ...
];

const mobileSlides = [
  { id: 1, image: require('../assets/mobile-banners/1.png'), url: '/shop' },
  { id: 2, image: require('../assets/mobile-banners/2.png'), url: '/shop/glutashin-1200-' },
  { id: 3, image: require('../assets/mobile-banners/3.png'), url: '/shop/lemon-bottle' },
  { id: 4, image: require('../assets/mobile-banners/4.png'), url: '/shop/rentox-100-units' },
  { id: 5, image: require('../assets/mobile-banners/5.png'), url: '/shop/botulax-100-units' },
  { id: 6, image: require('../assets/mobile-banners/6.png'), url: '/shop/glutanex-tab' },
  { id: 7, image: require('../assets/mobile-banners/7.png'), url: '/shop/cartinex' },
  // ...
];

const Shop = () => {

    const isMobile = useMediaQuery({ maxWidth: 768 });
    usePageView(MIXPANEL_PAGE.SHOP);
    
    const navigate = useNavigate();
    const { addToCart, setIsCartOpen } = useCart();
    
    const [categories, setCatetories] = useState<any[]>([]);

    const [category, setCategory] = useState<null|string>(null);

    const [searchQuery, setSearchQuery] = useState('');

    const [products, setProducts] = useState<any[]>([]);

    const filteredProducts = useMemo(() => {
        let data:any[] = [];
        if (category && category !== '-1') {
            products.forEach((product) => {
                if (product.categories.includes(category)) {
                    data.push(product);
                }
            });
        }  else {
            data = products;
        }

        if (searchQuery) {
            data = data.filter((product) => product.name.toUpperCase().includes(searchQuery.toUpperCase()))
        }
        
        return data;
    }, [products, category, searchQuery])
    

    const fetchCategories = async () => {
        try {
            const data = await APIService.getCategories({ limit: 1000 });
            setCatetories(data.docs);
        } catch (e) {}
        
    }

    const fetchProducts = async () => {
        try {
            const data = await APIService.getProducts({ limit: 1000 });
            setProducts(data.docs);
        } catch (e) {}
      
    }

    useEffect(() => {
        fetchCategories();
        fetchProducts();
    }, [])

    if (!categories || !products) {
        return (
            <>
                <Navigation />
                <Root>
                    
                    <Layout
                        style={{
                            flex: 1,
                            width: '100%'
                        }}
                    >
                        <StyledSkeleton height={100} baseColor={PALE} />
                        <StyledSkeleton height={200} baseColor={LIGHT_PINK_ORANGE}/>
                        <StyledSkeleton height={300} baseColor={PALE}/>
                        <StyledSkeleton height={200} baseColor={LIGHT_PINK_ORANGE}/>
                    </Layout>
                </Root>
            </>
        )
    }
    
    return (
        <>
            <Navigation />
            <HeroBanner 
                slides={isMobile ? mobileSlides : desktopSlides}
                isMobile={isMobile}
            />
            <Root>
                <CategorySlider
                    categories={[{name: 'All Products', _id: '-1'}, ...categories]}
                    onSelect={(id: string, name: string) => {
                        setCategory(id);
                        MixpanelService.track(`${MIXPANEL_PAGE.SHOP} - ${MIXPANEL_ACTION.BUTTON_CLICK}`, {
                            item: 'Select Category',
                            category: name
                        });
                    }}
                />
                <Layout>
                    <SearchBar 
                        placeholder={'SEARCH PRODUCT...'}
                        onSearch={(value) => {
                            setSearchQuery(value);
                        }}
                    />
                    <HeadingTitle>
                        <p>
                            {`Products`.toUpperCase()} - ({filteredProducts.length})
                        </p>
                    </HeadingTitle>
                    <ProductGrid>
                        {filteredProducts.map((product) => (
                            <ProductCard 
                                name={product.name}
                                thumbnail={product?.images[0]}
                                units={product.units}
                                description={product.description}
                                onPress={() => {
                                    navigate('/shop/' + product.slug);
                                }}
                                onAdd={(unitType: string) => {
                                    MixpanelService.track(`${MIXPANEL_PAGE.SHOP} - ${MIXPANEL_ACTION.BUTTON_CLICK}`, {
                                           item: 'Add To Cart',
                                        product: product.name,
                                        type: unitType
                                    });
                                    addToCart(product, unitType);
                                    setIsCartOpen(true);
                                }}
                            />
                        ))}
                    </ProductGrid>
                </Layout>
            </Root>
        </>
    )
}


const StyledSkeleton = styled(Skeleton)`
    flex: 1;
    margin: 0.5rem 0rem;
`;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0rem 1rem;
`;

const HeadingTitle = styled.div`
    p {
        font-family: 'Poppins-Medium';
        font-size: 0.8rem;
    }

    @media (min-width: 768px) {
        p {
            font-size: 1rem;
        }
    }
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;

  @media(min-width: 768px) {
    max-width: 1200px; /* or 1280px */
    margin: 0 auto;
    padding: 1rem 1rem; /* optional side padding */
  }
`;

const ProductGrid = styled.div`

  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr); // tablet
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr); // desktop
  }

  @media (min-width: 1440px) {
    grid-template-columns: repeat(5, 1fr); // large desktop
  }
`;

export default Shop;